import { useCallback, useEffect, useState } from "preact/hooks";

import { fetchShareableUsers } from "../lib/api";
import type { ShareableUserOut } from "../types";

// Sprint-018 E4 (TD-044) — the member-owner "Who's helping" picker's data
// layer. `GET /me/shareable-users` (security review `security-review-e4-
// member-roster-2026-07-11.md`, SIGNED) is session-cookie-only and returns
// every ACTIVE user except the caller, projected to exactly `{id, name}`.
// `enabled` is true ONLY for a plain member who owns the project being
// shared (see `WhosHelping.tsx`'s `isMemberOwner` prop) — a manager/admin
// session never calls this hook at all, and keeps its existing team-board-
// sourced roster unchanged.

export interface UseShareableUsersResult {
  users: ShareableUserOut[];
  loading: boolean;
  error: string | null;
}

export function useShareableUsers(enabled: boolean): UseShareableUsersResult {
  const [users, setUsers] = useState<ShareableUserOut[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchShareableUsers();
      setUsers(rows);
    } catch {
      setError("Couldn't load who can help.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { users, loading, error };
}
